import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, LogIn } from 'lucide-react';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../lib/ThemeContext';
import i18n from '../../lib/i18n';
import LoginModal from '../auth/LoginModal';
import './ReportModal.css';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameType: string;
  questionId?: string | number;
  questionText?: string;
  onReportSubmitted?: () => void;
}

interface ReportData {
  gameType: string;
  questionId?: string | number;
  questionText?: string;
  reportReason: string;
  improvementSuggestion: string;
  timestamp: string;
  status: 'Reported' | 'In Review' | 'In Progress' | 'No Action' | 'On Hold' | 'Completed';
  reporterId: string;
  reporterEmail: string;
  // Enhanced fields for better admin management
  collectionId?: string; // The actual Firestore collection name
  language?: string; // Language of the question
  originalQuestionData?: any; // Store original question data for context
  adminNotes?: string; // For admin notes during review
  reviewedAt?: string; // When admin reviewed
  reviewedBy?: string; // Admin who reviewed
  action?: string; // Action taken (approve, reject, edit, etc.)
  reportId: string; // Unique identifier for the report
  createdAt: any; // Firestore Timestamp
  updatedAt: any; // Firestore Timestamp
}

const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  gameType,
  questionId,
  questionText,
  onReportSubmitted
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const [reportReason, setReportReason] = useState('');
  const [improvementSuggestion, setImprovementSuggestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const reportReasons = [
    'inappropriate_content',
    'offensive_language',
    'inappropriate_for_age',
    'spam_or_repetitive',
    'grammar_or_spelling',
    'other'
  ];

  const getReasonLabel = (reason: string) => {
    const reasonLabels: Record<string, string> = {
      inappropriate_content: t('report.reasons.inappropriateContent') || 'Inappropriate Content',
      offensive_language: t('report.reasons.offensiveLanguage') || 'Offensive Language',
      inappropriate_for_age: t('report.reasons.inappropriateForAge') || 'Inappropriate for Age Group',
      spam_or_repetitive: t('report.reasons.spamOrRepetitive') || 'Spam or Repetitive',
      grammar_or_spelling: t('report.reasons.grammarOrSpelling') || 'Grammar or Spelling Issues',
      other: t('report.reasons.other') || 'Other'
    };
    return reasonLabels[reason] || reason;
  };

  const handleSubmit = async () => {
    if (!reportReason.trim()) {
      setError(t('report.errors.selectReason') || 'Please select a reason for reporting');
      return;
    }

    // Check if user is authenticated
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Get current language for the correct collection
      const currentLanguage = i18n.language || 'en';
      const collectionName = `${gameType}V1_${currentLanguage}`;

      const reportId = `RPT-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
      const reportData: ReportData = {
        gameType,
        questionId,
        questionText,
        reportReason,
        improvementSuggestion: improvementSuggestion.trim(),
        timestamp: new Date().toISOString(),
        status: 'Reported',
        reporterId: user.uid,
        reporterEmail: user.email || 'unknown@email.com',
        // Enhanced fields for better admin management
        collectionId: collectionName,
        language: currentLanguage,
        reportId,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('ReportModal: Received questionId:', questionId);
      console.log('ReportModal: QuestionId type:', typeof questionId);
      console.log('Report data prepared:', reportData);
      
      // Add to questionReports collection
      console.log('Creating report document...');
      const reportRef = await addDoc(collection(db, 'questionReports'), reportData);
      console.log('Report document created:', reportRef.id);

      // Note: Questions are now served from CDN, so we don't update original question documents
      // The report is stored in the questionReports collection for admin review
      console.log('Question report stored in questionReports collection');

      console.log('Report submitted successfully:', reportRef.id);
      onReportSubmitted?.();
      onClose();
      
      // Reset form
      setReportReason('');
      setImprovementSuggestion('');
    } catch (err) {
      console.error('Error submitting report:', err);
      setError(t('report.errors.submissionFailed') || 'Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setReportReason('');
    setImprovementSuggestion('');
    setError('');
    setShowLoginPrompt(false);
    onClose();
  };

  const handleLoginRequest = () => {
    // Open the login modal
    setShowLoginModal(true);
  };

  const handleLoginModalClose = () => {
    setShowLoginModal(false);
    setShowLoginPrompt(false);
    onClose();
  };

  const handleLoginSuccess = () => {
    // User successfully logged in, close both modals
    setShowLoginModal(false);
    setShowLoginPrompt(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal overlay */}
      <div
        className="report-modal-overlay"
        onClick={handleCancel}
      />
      
      {/* Modal content */}
      <div className="report-modal-overlay">
        <div 
          className="report-modal-content bg-white dark:bg-gray-800"
          style={{
            color: currentTheme.textPrimary,
            border: `1px solid ${currentTheme.border}`
          }}
        >
          {/* Header */}
          <div 
            className="report-modal-header border-gray-200 dark:border-gray-600"
            style={{
              borderBottom: `1px solid ${currentTheme.border}`
            }}
          >
            <div className="flex items-center justify-center flex-1">
              <AlertTriangle className="w-4 h-4 text-red-500 mr-2" />
              <h2 
                className="text-center text-gray-900 dark:text-white text-sm font-medium"
              >
                {t(`games.${gameType}.title`) || t(`games.${gameType}Title`) || gameType}
              </h2>
            </div>
            <button
              onClick={handleCancel}
              className="report-modal-close absolute right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="report-modal-body">
            {showLoginPrompt ? (
              /* Login Prompt */
              <div className="report-section">
                <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                  <LogIn className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                  <h3 className="text-gray-700 dark:text-gray-300" style={{ marginBottom: '1rem' }}>
                    {t('report.loginRequired') || 'Login Required'}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400" style={{ marginBottom: '1.5rem', lineHeight: '1.5' }}>
                    {t('report.loginRequiredMessage') || 'You need to be logged in to submit a report. Please login and try again.'}
                  </p>
                  <button
                    onClick={handleLoginRequest}
                    className="report-btn report-btn-submit"
                    style={{ maxWidth: '200px' }}
                  >
                    {t('report.goToLogin') || 'Go to Login'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Report Reason Section */}
                <div className="report-section">
                  <h3 className="text-gray-900 dark:text-white">
                    {t('report.reason') || 'Reason for Report'}
                  </h3>
                  <div className="report-reasons">
                    {reportReasons.map((reason) => (
                      <label key={reason} className="report-reason-label">
                        <input
                          type="radio"
                          name="reportReason"
                          value={reason}
                          checked={reportReason === reason}
                          onChange={(e) => setReportReason(e.target.value)}
                          className="report-reason-input"
                        />
                        <span className="text-gray-900 dark:text-white">
                          {getReasonLabel(reason)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Improvement Suggestion Section */}
                <div className="report-section">
                  <h3 className="text-gray-900 dark:text-white">
                    {t('report.improvementSuggestion') || 'Suggest Improvements'}
                  </h3>
                  <textarea
                    value={improvementSuggestion}
                    onChange={(e) => setImprovementSuggestion(e.target.value)}
                    placeholder={t('report.improvementPlaceholder') || 'Please provide suggestions for improvement (optional)...'}
                    className="report-textarea bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                    maxLength={200}
                  />
                  <div className="report-char-count text-gray-500 dark:text-gray-400">
                    {improvementSuggestion.length}/200
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="report-error">
                    <p>{error}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="report-modal-footer bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600">
            <button
              onClick={handleCancel}
              disabled={isSubmitting}
              className="report-btn report-btn-cancel"
            >
              {t('report.cancel') || 'Cancel'}
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="report-btn report-btn-submit"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="report-spinner" />
                  {t('report.submitting') || 'Submitting...'}
                </span>
              ) : (
                t('report.submit') || 'Submit'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onRequestClose={handleLoginModalClose}
        onLogin={handleLoginSuccess}
      />
    </>
  );
};

export default ReportModal; 